import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, Divider, Stack } from '@curves/ui-base';
import { ProductInfo } from '@curves/ui-shared';
import { useHistory, useLocation } from 'react-router-dom';
import { useMainLayoutContext } from '@Local/Components/Layout/Context/MainLaydoutContext';
import { FOOTER_TYPE, HEADER_TYPE, THEME_TYPE } from '@Constants/dataEnums';
import { MEMBER_CYCLE_PURCHASE_ROUTER, MEMBER_CYCLE_PURCHASE_CONFIRM_ROUTER } from '@Router/routerPath';
import { CyclePurchaseStepper } from '@Local/CyclePurchase/Components';
import { isCyclePurchaseQtyEditable } from '@Local/CyclePurchase/staticData';
import useCyclePurchaseCart from '@Local/CyclePurchase/hooks/useCyclePurchaseCart';
import useMemberCyclePurchaseApi from '@Apis/useMemberCyclePurchaseApi';

/**
 * 循環訂單選品頁
 * 顯示可選購商品清單，以目前訂單數量（sourceCart）初始化購物車後供會員調整。
 * 完成選品後導向確認頁（CyclePurchaseConfirmPage）。
 * 入口：CyclePurchasePage → handleEdit（location.state: { order, sourceCart }）
 */
const CyclePurchaseProductSelectPage = () => {
    const { setHeaderType, setFooterType, setThemeType, setHeaderContent } = useMainLayoutContext();
    const history = useHistory();
    const location = useLocation();
    const { order, sourceCart = {} } = location.state || {};
    const { getMemberCyclePurchaseProductsApi } = useMemberCyclePurchaseApi();

    // 功能：可選購商品清單（由 API 取得）
    const [products, setProducts] = useState([]);

    // 功能：購物車狀態管理（商品數量、合計、是否可送出）
    const { cart, cartSummary, totalItems, totalPrice, hasCartItems, addItem, removeItem, resetCart } =
        useCyclePurchaseCart(products);

    // 功能：可選總數上限＝原訂單各口味數量加總（當初幾項只能換幾項，總和不可超過）
    const maxItems = useMemo(() => Object.values(sourceCart).reduce((sum, qty) => sum + (qty || 0), 0), [sourceCart]);

    // 功能：已選總數是否與原訂單相符
    // 後端變更口味要求各品類數量與原訂單完全相同（不符回 CO026），
    // 因此未滿或超過都不可送出，直接鎖住「確認變更」並顯示提示
    const isOverLimit = totalItems > maxItems;
    const isQtyMatched = totalItems === maxItems;

    // 功能：依商品類型（productType）分組顯示，標題取 productTypeText
    // 類型的顯示順序與組內商品順序皆沿用後端回傳順序（以類型首次出現的位置為準），不另行排序
    // 用 Map 而非物件累積：Map 保證插入順序，數字 key 不會被 JS 物件重排成數值升冪
    // isQtyEditable 以組為單位計算即可：分組鍵就是 productType，同組商品判定結果必然相同
    const productSections = useMemo(() => {
        const sectionMap = new Map();

        products.forEach((product) => {
            if (!sectionMap.has(product.productType)) {
                sectionMap.set(product.productType, {
                    productType: product.productType,
                    productTypeText: product.productTypeText,
                    isQtyEditable: isCyclePurchaseQtyEditable(product.productType),
                    items: [],
                });
            }

            sectionMap.get(product.productType).items.push(product);
        });

        return Array.from(sectionMap.values());
    }, [products]);

    // 功能：守衛（未傳訂單時轉回列表）＋設定版面
    useEffect(() => {
        setHeaderType(HEADER_TYPE.DEFAULT);
        setFooterType(FOOTER_TYPE.HIDE);
        setThemeType(THEME_TYPE.DEFAULT);
        setHeaderContent(<CyclePurchaseStepper activeStep={0} onBack={() => history.goBack()} />);

        if (!order) history.replace(MEMBER_CYCLE_PURCHASE_ROUTER);

        return () => setHeaderContent(null);
    }, [history, order, setHeaderType, setFooterType, setThemeType, setHeaderContent]);

    // 功能：載入可選購商品清單
    useEffect(() => {
        let isCancelled = false;
        const fetchProducts = async () => {
            try {
                const data = await getMemberCyclePurchaseProductsApi();
                if (!isCancelled && data) setProducts(data);
            } catch (e) {
                if (isCancelled) return;
            }
        };
        fetchProducts();
        return () => {
            isCancelled = true;
        };
    }, [getMemberCyclePurchaseProductsApi]);

    // 功能：商品載入後以訂單原始數量初始化購物車（僅在 products 首次填入時觸發）
    useEffect(() => {
        if (products.length === 0) return;
        resetCart(sourceCart);
    }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!order) return null;

    // 功能：本次不變更，返回上頁
    const handleSkip = () => history.goBack();

    // 功能：確認選品，帶入當前選品明細前往確認頁
    const handleConfirm = () => {
        history.replace(MEMBER_CYCLE_PURCHASE_CONFIRM_ROUTER, { order, cartSummary });
    };

    return (
        <Box className="cycle-purchase-product-select-page">
            <Box className="container">
                <Typography className="cp-ps-title">更換訂購商品</Typography>

                {/* 商品清單（依商品類型分組） */}
                <Box className="cp-ps-product-list">
                    {productSections.map((section) => (
                        <Box key={section.productType} className="cp-ps-product-section">
                            {section.productTypeText && (
                                <Typography className="cp-ps-section-title">
                                    {section.productTypeText}
                                    {/* 數量鎖定的類型在標題直接標註，讓會員知道不是操作失效 */}
                                    {!section.isQtyEditable && '（數量不可變更）'}
                                </Typography>
                            )}
                            {section.items.map((product, index, arr) => {
                                const qty = cart[product.productID] || 0;

                                return (
                                    <React.Fragment key={product.productID}>
                                        <Box className={`cp-ps-product-card${qty > 0 ? ' is-selected' : ''}`}>
                                            <ProductInfo
                                                product={{
                                                    name: product.productName,
                                                    currentPrice: product.price,
                                                    imageUrl: product.pictureSrc,
                                                }}
                                                imageSize={72}
                                            />
                                            {/* 數量鎖定的類型不渲染加減按鈕（而非 disabled），只顯示原訂單數量；
                                                未包含在原訂單中的口味以「－」表示，避免整組出現一排 x0 */}
                                            {section.isQtyEditable ? (
                                                <Box className="cp-qty-control">
                                                    <button
                                                        className="cp-qty-btn"
                                                        onClick={() => removeItem(product.productID)}
                                                        disabled={qty === 0}
                                                    >
                                                        −
                                                    </button>
                                                    <Typography component="span" className="cp-qty-value">
                                                        {qty}
                                                    </Typography>
                                                    <button
                                                        className="cp-qty-btn"
                                                        onClick={() => addItem(product.productID)}
                                                    >
                                                        +
                                                    </button>
                                                </Box>
                                            ) : (
                                                <Typography component="span" className="cp-qty-locked">
                                                    {qty > 0 ? `x${qty}` : '－'}
                                                </Typography>
                                            )}
                                        </Box>
                                        {/* 分隔線僅出現在同組商品之間，組末不加以免與下一組標題相連 */}
                                        {index < arr.length - 1 && <Divider />}
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    ))}
                </Box>

                {/* 固定底部：已選摘要 + 操作按鈕 */}
                <Box className="cp-ps-footer">
                    <Box className="cp-ps-summary-row">
                        <Typography className="cp-ps-summary-label">已選</Typography>
                        <Typography className="cp-ps-summary-val">
                            {totalItems} / 共 {maxItems} 項・合計 ${totalPrice.toLocaleString()}
                        </Typography>
                    </Box>
                    {/* 數量不符時說明原因，避免會員不知道「確認變更」為何是鎖定狀態 */}
                    {!isQtyMatched && (
                        <Typography className="cp-ps-summary-hint">
                            {isOverLimit ? '已超過' : '尚未達到'}原訂單數量（共 {maxItems} 項），請調整後再送出
                        </Typography>
                    )}
                    <Stack direction="row" spacing={1} className="cp-ps-btn-row">
                        <Button variant="outlined" onClick={handleSkip} className="cp-ps-skip">
                            本次不變更
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirm}
                            disabled={!hasCartItems || !isQtyMatched}
                            className="cp-ps-confirm"
                        >
                            確認變更
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};

export default CyclePurchaseProductSelectPage;
